import { isUndefined } from 'lodash';
import { AbstractFormatter } from '../Formatter';
import { TypedValue, DataType } from '../DataPrimitive';
import { getDataTypeForPoint, isNumber } from '../utils/types';
import { setDefaultValue } from '../utils/formatterUtils';
import { DataPoint } from '../DataPoint';
import EncodingExecutor from '../EncodingExecutor';

export interface RangesConfig {
    from?: number;
    value: any;
    to?: number;
}

const { hasOwnProperty } = Object.prototype;

/**
 * Formatter function that takes the list of ranges and a defaultValue (if no range is found)
 * and returns a function that takes a value and returns the range value if found, or the defaultValue if present.
 *
 * If neither are present, it returns the original value.
 *
 * The range fitting follows this criteria: `range.from <= value < range.to`
 *
 * A range can be defined as either a closed bound range: `{ from: 10, to: 20, value: 'foo' }`
 * or an open bound range:
 * `{ to: 20, value: 'bar' }` (open lower bound)
 * `{ from: 100, value: 'oof' }` (open upper bound)
 *
 * ```js
 *   <SampleViz
 *      context={{
 *          colorThresholds: [
 *              {
 *                  from: 1321,
 *                  value: '#00FFFF',
 *              },
 *              {
 *                  to: 1321,
 *                  value: '#FF00FF',
 *              },
 *          ],
 *      }}
 *      options={{
 *          valueOption: '> primary | seriesByIndex(0) | lastPoint()', // returns 103
 *          colorOption: '> deltaValue | rangeValue(colorThresholds)', // returns '#FF00FF'
 *      }}
 *      dataSources={{
 *          primary: {
 *              data: {
 *                  columns: [
 *                      ['1', '103'], ['2018-08-19T00:00:00.000+00:00', '2018-08-20T00:00:00.000+00:00'],
 *                  ],
 *                  fields: [{ name: 'foo', }, { name: '_time' }],
 *              }
 *          },
 *      }}
 *  />
 * ```
 * @extends AbstractFormatter<DataType, DataType>
 */
export class RangeValue extends AbstractFormatter<DataType, DataType> {
    // although the typical usage is that the input is  number, this formatter DOES accept an DataType
    private readonly ranges: RangesConfig[];

    private readonly defaultValue: any;

    constructor(ranges: RangesConfig[], defaultValue?: any) {
        super();
        this.ranges = EncodingExecutor.rawTree(ranges); // insure we can handle ranges that had DSL expressions shoved into them
        this.defaultValue = setDefaultValue(defaultValue);
    }

    private findMatchingRange(floatValue: number): any {
        for (const range of this.ranges) {
            const haFrom = hasOwnProperty.call(range, 'from');
            const haTo = hasOwnProperty.call(range, 'to');
            // normalize from/to: config values may arrive as string-typed numbers (e.g. from: '2')
            const fromVal = haFrom ? Number(range.from) : undefined;
            const toVal = haTo ? Number(range.to) : undefined;
            if (
                // open upper bound: value >= from with no upper bound
                (haFrom && !haTo && floatValue >= fromVal) ||
                // inbetween: from (inclusive) to to (exclusive)
                (haFrom && haTo && floatValue >= fromVal && floatValue < toVal) ||
                // open lower bound: value < to with no lower bound
                (!haFrom && haTo && floatValue < toVal)
            ) {
                return range.value;
            }
        }
        return undefined;
    }

    protected formatTypedValue(p: DataPoint<'number'>): TypedValue<DataType> {
        const { type, value } = p.getValue();
        if (!isNumber(value)) {
            // eslint-disable-next-line no-console
            console.warn(`type '${type}' with value '${value}' is not a valid input to rangeValue`);
        }
        // if no range is found and valid value, return defaultValue if present, otherwise return the original value
        const defaultRangeValue =
            isUndefined(this.defaultValue) && !isNumber(value) ? value : this.defaultValue;
        const rangeValueResult = isNumber(value)
            ? this.findMatchingRange(Number.parseFloat(value))
            : undefined;
        const updatedValue = isUndefined(rangeValueResult) ? defaultRangeValue : rangeValueResult;
        const updatedType = getDataTypeForPoint(updatedValue);
        return { type: updatedType, value: updatedValue };
    }
}
